import { useCallback, useEffect, useRef } from 'react';

import {
    useGetApiNotionPhasePhaseIdUserStories,
    useGetApiNotionProjectProjectIdUserStories
} from '../../../../api/notion/notion';
import { useGetApiProductiveServiceNames } from '../../../../api/productive/productive';
import type { BlockViewProps } from '../../core/types';
import type { CostEstimateData } from '../../directives/components/CostEstimateForm';
import { CostEstimateForm } from '../../directives/components/CostEstimateForm';
import { useDocumentContext } from '../../directives/contexts/DocumentContext';

export function CostEstimateBlockView({
    data,
    onDataChange,
    onDelete,
    readOnly
}: BlockViewProps<CostEstimateData>) {
    const { document: documentDto } = useDocumentContext();
    const phaseId = documentDto.notionPhaseId;

    const { data: projectUserStories, refetch: refetchProjectUserStories } =
        useGetApiNotionProjectProjectIdUserStories(
            documentDto.projectId,
            undefined,
            { query: { enabled: !!documentDto.projectId && !phaseId } }
        );

    const { data: phaseUserStories, refetch: refetchPhaseUserStories } =
        useGetApiNotionPhasePhaseIdUserStories(phaseId ?? '', undefined, {
            query: { enabled: !!phaseId }
        });

    const userStories = phaseId ? phaseUserStories : projectUserStories;
    const refetchUserStories = phaseId
        ? refetchPhaseUserStories
        : refetchProjectUserStories;

    const { data: serviceNames, refetch: refetchServiceNames } =
        useGetApiProductiveServiceNames();

    // Debounce writes back through adapter
    const saveTimeoutRef = useRef<NodeJS.Timeout>();
    const lastSavedRef = useRef<string>('');

    const handleChange = useCallback(
        (newData: CostEstimateData) => {
            const json = JSON.stringify(newData);
            if (json === lastSavedRef.current) return;

            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(() => {
                onDataChange(newData);
                lastSavedRef.current = json;
            }, 500);
        },
        [onDataChange]
    );

    useEffect(() => {
        return () => clearTimeout(saveTimeoutRef.current);
    }, []);

    const handleRefreshData = useCallback(async () => {
        await Promise.all([refetchUserStories(), refetchServiceNames()]);
    }, [refetchUserStories, refetchServiceNames]);

    const handleDelete = useCallback(() => {
        if (window.confirm('Delete this cost estimate?')) {
            onDelete();
        }
    }, [onDelete]);

    if (readOnly) {
        return (
            <CostEstimateForm
                data={data}
                onChange={() => { /* read-only */ }}
                userStories={userStories ?? []}
            />
        );
    }

    return (
        <CostEstimateForm
            data={data}
            onChange={handleChange}
            userStories={userStories ?? []}
            availableServices={serviceNames?.map(
                (service: { name: string }) => service.name
            )}
            onDelete={handleDelete}
            onRefreshData={handleRefreshData}
        />
    );
}
